import { createContext } from 'preact';
import { useContext, useEffect } from 'preact/hooks';
import { useSignal } from '@preact/signals';
import translations from './translations.json';
import currencies from './currencies.json';
import useConfig from '../config/config.context';
import CaasStorageKeys from '../constants/caas.storage.keys';
import { LanguageCode, LanguageId, getOrganizationLanguage } from './language';
import useOrganizationsConfig from '../config/organizations.context';
import LoadingScreen from '../components/shared/LoadingScreen/LoadingScreen';

const I18nContext = createContext({});

const DefaultLanguage = LanguageCode.ENGLISH;

function I18nProvider({ children }) {
  const { config } = useConfig();
  const { organizations, loading } = useOrganizationsConfig();
  const language = useSignal(
    config[CaasStorageKeys.CONFIG]?.lang || DefaultLanguage,
  );

  function t(key, replacements = {}) {
    const value = translations[language.value][key] || key;

    if (!value) {
      console.warn(`Couldn't get ${language.value} value for key: ${key}`);
    }

    let translated = value || key;
    Object.keys(replacements).map(k => {
      translated = translated.replace(k, replacements[k]);
    });

    return translated;
  }

  function getOrganization(organizationCode) {
    if (!organizations || organizations?.length === 0) {
      console.warn(
        `Could not get name of organization with code ${organizationCode}. Returning the code itself to avoid break`,
      );
      return organizationCode;
    }

    const organizationLanguage = getOrganizationLanguage(language.value);
    const organization = organizations.filter(
      org => org.organizationCode === organizationCode?.toUpperCase(),
    );

    if (organization.length === 1) {
      return organization[0][organizationLanguage];
    }

    console.warn(
      `Could not get name of organization with code ${organizationCode}. Returning the code itself to avoid break`,
    );

    return organizationCode;
  }

  function getCurrency(currency) {
    return (
      currencies[language.value][currency] ||
      currencies[language.value]['default']
    );
  }

  function setLanguage(_language) {
    language.value = _language;
  }

  function getLanguageId() {
    return LanguageId[language.value];
  }

  useEffect(() => {
    const html = document.querySelector('html');

    html.setAttribute('lang', language.value);
    if (language.value.startsWith('ar')) {
      html.setAttribute('dir', 'rtl');
    }
  }, [language.value]);

  return (
    <>
      {loading && <LoadingScreen />}
      {!loading && (
        <I18nContext.Provider
          value={{
            language: language.value,
            setLanguage,
            t,
            getOrganization,
            getCurrency,
            getLanguageId,
          }}
        >
          {children}
        </I18nContext.Provider>
      )}
    </>
  );
}

export { I18nContext, I18nProvider };

export default function useTranslation() {
  const {
    language,
    setLanguage,
    t,
    getOrganization,
    getCurrency,
    getLanguageId,
  } = useContext(I18nContext);
  return {
    language,
    setLanguage,
    t,
    getOrganization,
    getCurrency,
    getLanguageId,
  };
}
